import React, { ComponentProps } from 'react';
import { Box } from '@wix/design-system';
import { createClient } from '@wix/sdk';
import { useControlService } from '../../common/atomic/hooks';
import type { AggregatedStyleValues } from '../../common/abstractStyles/types';
import {
  mapBorderEvent,
  mapBorderProps,
  type BorderChangeEvent,
} from './BorderControlsAdapter';
import { ComponentsPlatformService } from '@wix/editor-platform-manifest-services';
import { useBI } from '../../hooks/useBI';
import { StylesDerivedNativeActions } from '@wix/editor-platform-host-integration-apis';
import { GlobalDesignStyleGroupMetadata } from '../../types';

type BorderControlsProps = {
  values: AggregatedStyleValues;
  updateStyles: (changes: Record<string, string>) => Promise<void>;
  componentsAPI: ComponentsPlatformService;
  sdkClient: ReturnType<typeof createClient>;
  metadata?: GlobalDesignStyleGroupMetadata;
};

/**
 * BorderControls component for Global Design panel.
 *
 * Simple wrapper that:
 * 1. Retrieves Border control from service
 * 2. Uses mapBorderProps to map: values.decomposed → Border control props
 * 3. Uses mapBorderEvent to map: Border onChange → atomic CSS properties
 * 4. Passes atomic properties to updateStyles for smart propagation
 *
 * Why atomic properties + updateStyles:
 * - mapBorderEvent detects what changed (width/style/color)
 * - Returns ALL atomic per-side properties (borderTopColor, borderRightColor, etc.)
 * - updateStyles handles smart propagation (siblings, parent recomposition)
 *
 * Keep components simple - adapter + engine do the work!
 */
export const BorderControls: React.FC<BorderControlsProps> = ({
  values,
  updateStyles,
  componentsAPI,
  sdkClient,
  metadata,
}) => {
  const service = useControlService();
  const { trackValueUpdate } = useBI();

  // Type assertion needed: 'border' exists in CssPropertyEnumCssPropertyType at runtime
  // but TypeScript's PropertyKey_2 type definition doesn't include it

  const [BorderControl, setBorderControl] =
    React.useState<React.ComponentType<any> | null>(null);

  React.useEffect(() => {
    if (!BorderControl) {
      const control = service.getControl(
        'border' as any,
        'cssPropertyType',
      ).Component;
      setBorderControl(() => control);
    }
  }, [service]);

  const borderProps = React.useMemo(() => mapBorderProps(values), [values]);

  const handleChange = React.useCallback(
    (fullValue: string, _partials: unknown[]) => {
      const event: BorderChangeEvent = {
        fullValue,
      };

      const changes = mapBorderEvent(event, componentsAPI);

      updateStyles(changes);

      trackValueUpdate(StylesDerivedNativeActions.borderStyles, {
        prevValues: JSON.stringify(borderProps.value),
        newValues: JSON.stringify(changes),
        targetComponents: metadata?.targetComponents,
      });
    },
    [
      componentsAPI,
      updateStyles,
      trackValueUpdate,
      borderProps,
      metadata?.targetComponents,
    ],
  );

  // Create panelActions with selectColor using the SDK client
  const panelActions = React.useMemo(() => {
    if (!BorderControl) {
      return {};
    }

    return {
      selectColor: (
        options: ComponentProps<
          typeof BorderControl
        >['panelActions']['selectColor'],
      ) => {
        return sdkClient.element.selectColor({
          ...options,
          showBrand: metadata?.isBrandColorSupported || false,
        });
      },
    };
  }, [BorderControl, sdkClient, metadata?.isBrandColorSupported]);

  const borderControlElement = BorderControl
    ? React.createElement(BorderControl, {
        ...borderProps,
        onChange: handleChange,
        panelActions,
      })
    : null;

  return <Box direction="vertical">{borderControlElement}</Box>;
};
